import {
  Loading,
  MAutocomplete,
  MButton,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import { Box, Grid, Typography } from "@mui/material";
import {
  useEquipment,
  useFactorGeneric,
  useLaboratory,
  useTypesUnits,
  useUser,
} from "@services/servicesGenericsTables";
import React, { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import useFilteredProcedures from "@app/pages/samples/hook/useFilteredProcedures";
import dayjs from "dayjs";
import { parseRptFileExe } from "../GT/fileUploadAreaExe";
import { useBackground } from "@app/pages/background/utils/useBackground";
import { ResultsTypes } from "@app/types/samples.types";
import OutDataGT from "./outData";
import FileUploadAreaExeGT from "./fileUploadAreaExe";
import { lastDetector } from "../IG/helpers";

const CalculationGT = () => {
  const { setValue, getValues, watch } = useFormContext();
  const { userList } = useUser();
  const { typesUnitsis } = useTypesUnits();
  const {
    fetchAll: fetchFactorGeneric,
    genericFactorList,
    loading,
  } = useFactorGeneric();
  const { fondosList } = useBackground();

  const { laboratoriesList } = useLaboratory();
  const { equipmentList } = useEquipment();
  const {
    currentCalculation,
    importFileFromRoute,
    loadingFile,
    loadingResults,
    saveResultCalculate,
  } = useAnalysis();

  const findBackground = useMemo(() => {
    const selectedTypeAnalysis = getValues("typeAnalysis")?.id;
    const equipementAviables = getValues("counting_equipment") || [];
    return fondosList.find((fondo) => {
      return (
        fondo.analysis_type?.id === selectedTypeAnalysis &&
        equipementAviables.some((equip) => equip.id === fondo.equipment?.id)
      );
    });
  }, [
    fondosList,
    getValues("typeAnalysis")?.id,
    getValues("counting_equipment"),
  ]);

  const watchIntegralSpectrumSampleAccounts = watch(
    "integral_spectrum_sample_accounts"
  );
  const watchCountingTime = watch("total_sample_counting_time");

  useEffect(() => {
    const avgIntegralFondo = Number(findBackground?.avg_integral_fondo) || 0;

    const calculateCpsSpectrumAreaShow =
      (watchIntegralSpectrumSampleAccounts -
        avgIntegralFondo * watchCountingTime) /
      watchCountingTime;

    const setVAlue =
      !isNaN(calculateCpsSpectrumAreaShow) && calculateCpsSpectrumAreaShow
        ? calculateCpsSpectrumAreaShow?.toExponential(2)
        : 0;
    setValue("cps_spectrum_area_shows", setVAlue);
  }, [findBackground, watchCountingTime, watchIntegralSpectrumSampleAccounts]);

  const filteredCalculation = useFilteredProcedures("CAL");

  useEffect(() => {
    setValue("calculation_date_countig_file", undefined);
    setValue("pico_counts_341", undefined);
    setValue("pico_counts_911", undefined);
    setValue("detector_file", undefined);
    setValue("calculation_equipment_file", undefined);
    setValue("calculation_date_countig_file_exe", undefined);
    setValue("calculation_detector", undefined);
    setValue("calibration_source_certificate", undefined);
    setValue("total_sample_counting_time", undefined);
    setValue("calibration_efficiency_date", undefined);
    setValue("calculation_background", undefined);
    setValue("calculation_library", undefined);
  }, [watch("source_app_calculation_info")]); // eslint-disable-line react-hooks/exhaustive-deps

  const codeAnalysis = getValues("typeAnalysis")?.codigo;

  useEffect(() => {
    fetchFactorGeneric();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const route = getValues("source_app_calculation_info_file");
    const fetchData = async (linkRoute) => {
      const response = await importFileFromRoute(linkRoute, "TO_FORM");
      if (!response) return;

      const lines: string[] = parseRptFileExe(response);
      setValue("total_sample_counting_time", lines[1]);
      setValue("calculation_date_countig_file_exe", lines[2]);
      setValue("pico_counts_341", lines[4]);
      setValue("pico_counts_911", lines[5]);
      setValue("calculation_equipment_file", lines[3]);
    };
    if (currentCalculation.id && route) {
      fetchData(route);
    }
  }, [currentCalculation.id, getValues("source_app_calculation_info_file")]); // eslint-disable-line react-hooks/exhaustive-deps

  const memoGeoFactor = useMemo(() => {
    const findFactor = lastDetector(
      getValues("calculation_equipment"),
      equipmentList
    )?.factor;
    return findFactor;
  }, [genericFactorList, currentCalculation.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setValue("calculation_geometric_factor", memoGeoFactor);
  }, [memoGeoFactor]); // eslint-disable-line react-hooks/exhaustive-deps

  const textButtonCalculation = "Calcular";
  const handleCalculate = async () => {
    const resultData: ResultsTypes = {
      result: true,
      to_reports: watch("to_report") ?? true,
    };

    await saveResultCalculate(codeAnalysis as string, resultData);
  };

  return (
    <>
      {currentCalculation.index !== undefined && !loading && (
        <>
          <ResponsiveFormContainer columns={3}>
            <MAutocomplete
              key="calculation_analyst"
              name="calculation_analyst"
              options={userList}
              label="Seleccione un analista"
              getOptionLabel={(option) => option.username || ""}
              validation={{ required: "El analista es requerido" }}
            />
            <MDatePicker
              key="start_date_time"
              name="start_date_time"
              pickerType="datetime"
              label="Fecha y hora de inicio Toma de muestra"
              disabled
            />
            <MDatePicker
              key="end_date_time"
              name="end_date_time"
              pickerType="datetime"
              label="Fecha y hora fin Toma de muestra"
              disabled
            />
          </ResponsiveFormContainer>

          <Grid item xs={12} my={2}>
            <Typography variant="tagText">
              {"Datos ficha del procesamiento, preparación y contaje:"}
            </Typography>
          </Grid>
          <ResponsiveFormContainer columns={3}>
            <MTextField
              key={JSON.stringify(getValues("calculation_amount_sample"))}
              label="Cantidad de muestra"
              name="calculation_amount_sample"
              type="text"
              disabled
              fullWidth
            />
            <MAutocomplete
              key={JSON.stringify(getValues("calculation_units"))}
              name="calculation_units"
              options={typesUnitsis}
              label="Unidades"
              getOptionLabel={(option) =>
                `${option.codigo} | ${option.descripcion}`
              }
              disabled
            />
            <MAutocomplete
              key={JSON.stringify(getValues("calculation_equipment"))}
              name="calculation_equipment"
              options={equipmentList}
              label="Equipos y geometrías"
              getOptionLabel={(option) =>
                `${option.codigo} - ${
                  option.is_geometry 
                    ? (option.height || option.descripcion) 
                    : option.descripcion
                }` || ""
              }
              disabled
              multiple={true}
            />
            <MTextField
              key={JSON.stringify(getValues("geometry_height"))}
              label="Altura (mm)"
              name="geometry_height"
              type="text"
              disabled
              fullWidth
            />
            <MTextField
              key={JSON.stringify(getValues("calculation_tsie_sample"))}
              name="calculation_tsie_sample"
              label="Peso Húmedo/ Seco"
              type="text"
              disabled
              fullWidth
            />
            <MTextField
              name="total_sample_counting_time"
              label="Tiempo contaje muestra (s)"
              type="text"
              disabled
              fullWidth
            />
            <MTextField
              name="calculation_equipment_file"
              label="Detector"
              type="text"
              disabled
              fullWidth
            />
            <MTextField
              key={JSON.stringify(getValues("calculation_shared"))}
              name="calculation_shared"
              label="Compartida"
              type="text"
              disabled
              fullWidth
            />
          </ResponsiveFormContainer>

          <Grid item xs={12} my={2}>
            <Typography variant="tagText">{"Datos a cumplimentar:"}</Typography>
          </Grid>

          <ResponsiveFormContainer columns={4}>
            <MAutocomplete
              key={JSON.stringify(getValues("laboratory"))}
              name="laboratory"
              options={laboratoriesList}
              label="Seleccione un Laboratorio"
              getOptionLabel={(option) =>
                `${option.codigo} - ${option.descripcion}` || ""
              }
              validation={{ required: "El laboratorio es requerido" }}
            />
            <MAutocomplete
              key={JSON.stringify(getValues("calculation_procedure"))}
              name="calculation_procedure"
              options={filteredCalculation}
              label="Procedimiento MLPMA"
              getOptionLabel={(option) => `${option.numop}` || ""}
            />

            <MTextField
              key={JSON.stringify(
                getValues("calculation_self_absorption_factor")
              )}
              name="calculation_self_absorption_factor"
              label="Factor de autoabsorción"
              type="number"
              fullWidth
            />

            <MTextField
              key={JSON.stringify(getValues("calculation_geometric_factor"))}
              name="calculation_geometric_factor"
              label="Factor geométrico"
              type="number"
              fullWidth
              disabled
            />

            <MDatePicker
              name="calculation_date"
              pickerType="datetime"
              label="Fecha Cálculo"
              // minDate={dayjs(watch("calculation_counting_date"))}
            />
            <MTextField
              name="calculation_performance"
              label="Rendimiento"
              type="number"
              fullWidth
            />
            <MTextField
              name="integral_spectrum_sample_accounts"
              label="Cuentas integral espectro muestra"
              type="number"
              fullWidth
            />
            <MTextField
              name="cps_spectrum_area_shows"
              label="Cps área del espectro muestra"
              type="number"
              fullWidth
              disabled
            />
          </ResponsiveFormContainer>

          {loadingFile && <Loading />}
          {!loadingFile && (
            <>
              <Grid item xs={12} my={2}>
                <Typography variant="tagText">{"Fichero Muestra:"}</Typography>
              </Grid>
              <FileUploadAreaExeGT name="source_app_calculation_info_file" />
              <Grid item xs={12} my={2}>
                <Typography variant="tagText">{"Fichero Isotopos:"}</Typography>
              </Grid>
              <FileUploadAreaExeGT
                name="source_app_calculation_info_file_2"
                isRequired={false}
              />
            </>
          )}
          {currentCalculation.id !== undefined && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: 2,
              }}
            >
              <MButton
                onClick={handleCalculate}
                text={textButtonCalculation}
                color="secondary"
                loading={loadingResults}
                disabled={!!watch("idResultPreload")}
              />
            </Box>
          )}
          {currentCalculation.id !== undefined && <OutDataGT />}
        </>
      )}
    </>
  );
};

export default CalculationGT;
